import { useEffect, useRef, useState } from 'react'

import { useDb } from '@/lib/dbContext'

/**
 * Archived services are excluded from the live sync, so a detail page can
 * miss its record even though it exists on the server. When the live queries
 * have settled (`missing` is true only then) and the record is absent, ask
 * the adapter to hydrate it. `hydrating` stays true until that attempt
 * completes so callers keep showing their loading state instead of flashing
 * "not found".
 */
export function useEnsureServiceLoaded(
  type: 'callout' | 'session' | 'job',
  identity: string,
  missing: boolean,
) {
  const db = useDb()
  const key = `${type}:${identity}`
  const [settledKey, setSettledKey] = useState<string | null>(null)
  const inflightRef = useRef<string | null>(null)

  useEffect(() => {
    if (!missing || settledKey === key || inflightRef.current === key) return
    inflightRef.current = key
    let cancelled = false
    Promise.resolve(db.hydrateServiceDetails(type, identity))
      .catch((err) => {
        console.warn(`Failed to hydrate ${type} ${identity}`, err)
      })
      .finally(() => {
        inflightRef.current = null
        if (!cancelled) setSettledKey(key)
      })
    return () => {
      cancelled = true
    }
  }, [db, type, identity, missing, key, settledKey])

  return { hydrating: missing && settledKey !== key }
}
